import { Component } from '@angular/core';
import { Orders } from '../../interfaces';
import { DataServiceService } from '../../services/data-service.service';
import { filter, switchMap } from 'rxjs';
import { Router } from '@angular/router';


// Interfaces only used in load logistics
interface LoadBreakdown {
  unassigned: number;
  scheduled: number;
  inProgress: number;
  delivered: number;
}

interface LoadTypeStats {
  name: string;
  count: number;
  expanded: boolean;
  breakdown: LoadBreakdown;
}

@Component({
  selector: 'app-load-logistics',
  templateUrl: './load-logistics.component.html',
  styleUrl: './load-logistics.component.scss'
})

export class LoadLogisticsComponent {
  constructor(private dataServiceService: DataServiceService, private router: Router) { }

  loadTypes: LoadTypeStats[] = [];
  selectedDate: string = '';

  ngOnInit() {
    this.dataServiceService.selectedDate$.pipe(
      filter(date => !!date), // Ensure a valid date
      switchMap(date => {
        this.selectedDate = date; // Save the selected date as a string

        return this.dataServiceService.orders$.pipe(
          filter(orders => !!orders), // Ensure orders are valid
        );
      })
    ).subscribe({
      next: (filteredOrders) => {
        this.updateLoadTypeStats(filteredOrders); // Update the UI
      },
      error: (error) => {
        console.error('Error fetching orders:', error);
      }
    });
  }


  private updateLoadTypeStats(orders: Orders[]) {
    const loadTypeGroups = this.groupByLoadType(orders);

    this.loadTypes = Object.entries(loadTypeGroups).map(([loadType, orders]) => {
      const breakdown = this.getStatusBreakdown(orders);
      const totalCount = breakdown.unassigned +
        breakdown.scheduled +
        breakdown.inProgress +
        breakdown.delivered;  // Including delivered for current day

      return {
        name: loadType,
        count: totalCount,  // Includes delivered loads for current day
        expanded: false,
        breakdown: breakdown
      };
    });
  }


  private groupByLoadType(orders: Orders[]): { [key: string]: Orders[] } {
    return orders.reduce((groups, order) => {
      const loadType = order.load_type;
      if (!groups[loadType]) {
        groups[loadType] = [];
      }
      groups[loadType].push(order);
      return groups;
    }, {} as { [key: string]: Orders[] });
  }

  private getStatusBreakdown(orders: Orders[]): LoadBreakdown {

    const selectedDateStart = new Date(this.selectedDate + 'T00:00:00');
    selectedDateStart.setHours(0, 0, 0, 0);

    return orders.reduce((breakdown, order) => {
      switch (order.load_status.toLowerCase()) {
        case 'unassigned':
          breakdown.unassigned++;
          break;
        case 'scheduled':
          breakdown.scheduled++;
          break;
        case 'loaded':
          breakdown.inProgress++;
          break;
        case 'delivered':
          // Only count delivered if it was delivered on or after the selected date
          if (order.time_delivered != null) {
            const deliveryDate = new Date(order.time_delivered);
            if (deliveryDate >= selectedDateStart) {
              breakdown.delivered++;
            }
          }
          break;
      }
      return breakdown;
    }, {
      unassigned: 0,
      scheduled: 0,
      inProgress: 0,
      delivered: 0
    } as LoadBreakdown);
  }

  getTotalActiveLoads(): number {
    return this.loadTypes.reduce((total, load) => {
      // Add up all loads except delivered ones
      return total + (load.breakdown?.unassigned || 0) +
        (load.breakdown?.scheduled || 0) +
        (load.breakdown?.inProgress || 0);
    }, 0);
  }

  get loadLogisticsHeader(): string {
    const activeLoads = this.getTotalActiveLoads();
    return `Load Logistics (${activeLoads} Active)`;
  }

  viewOrderswithStatus(status: string, loadType: string) {
    this.router.navigate(['/orders'], { queryParams: { status: status, load_type: loadType } });
  }

}

